// App — Facet v2 information architecture.
//
// landing → portfolio ledger (the suite run) → flow ledger → staged canvas
// (stage ① verdict card → stage ② story & evidence), plus the diff and
// impact screens. The store's action flow is unchanged: SimulationShell
// still mounts → loadDefaultExample; scenario rows dispatch switchScenario;
// the portfolio dispatches suiteRunAll — the same dispatch sequence the
// headless harnesses (facet-sanity / facet-e2e) drive.
//
// Source setup (upload / live bridge) lives on the landing cards themselves
// (v2 design) — the old UploadDrawer and the TopologyEditor EDIT feature
// are gone.

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import './App.css';
import './v2.css';
import SimulationShell from './components/simulation/SimulationShell.tsx';
import SimulationCanvas from './components/simulation/SimulationCanvas.tsx';
import TopBar from './components/simulation/TopBar.tsx';
import PaletteLegend from './components/simulation/PaletteLegend.tsx';
import ZoomControls from './components/simulation/ZoomControls.tsx';
import PlaybackToolbar from './components/simulation/PlaybackToolbar.tsx';
import AssertionSummaryPanel from './components/simulation/AssertionSummaryPanel.tsx';
import EventInspectorPanel from './components/simulation/EventInspectorPanel.tsx';
import VerdictBanner from './components/simulation/VerdictBanner.tsx';
import ScenarioDiffView from './components/simulation/ScenarioDiffView.tsx';
import ImpactAnalysisPanel from './components/simulation/ImpactAnalysisPanel.tsx';
import WebSocketErrorOverlay from './components/simulation/WebSocketErrorOverlay.tsx';
import LandingView from './components/v2/LandingView.tsx';
import PortfolioLedger from './components/v2/PortfolioLedger.tsx';
import FlowLedger from './components/v2/FlowLedger.tsx';
import Stage1Card from './components/v2/Stage1Card.tsx';
import StoryRail from './components/v2/StoryRail.tsx';
import LiveBar from './components/v2/LiveBar.tsx';
import {
  selectSidebarOpen,
  selectSelectedNode,
  closeSidebar,
  openSidebar,
  toggleSidebar,
  selectAppView,
  selectScenarioStage,
  setScenarioStage,
} from './store/slices/uiSlice.ts';

/** Phone / compact-landscape: the same query App.css uses for the slide-over drawer. */
const COMPACT_QUERY = '(max-width: 767px), (max-height: 479px)';
function isCompactViewport(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(COMPACT_QUERY).matches
    : false;
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform);
const DETAIL_SHORTCUT = IS_MAC ? '\u2318K' : 'Ctrl+K';

/** Staged canvas — the existing canvas assembly under v2 choreography:
 *  full-bleed canvas, two HUD rows (verdict + zoom on top; legend + playback
 *  at the bottom), overlaid story rail, and the node-detail panel, which is
 *  open by default at stage 2 (collapsed on phones, where it is a slide-over
 *  drawer) and toggles with ⌘K / Ctrl+K. */
function ScenarioWorkspace() {
  const dispatch = useDispatch();
  const stage = useSelector(selectScenarioStage);
  const sidebarOpen = useSelector(selectSidebarOpen);
  const selectedNode = useSelector(selectSelectedNode);

  // Keyboard: 1 / 2 switch reading stages (never while typing in a field);
  // ⌘K / Ctrl+K toggles the detail panel (a modifier chord, so it also works
  // from a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        dispatch(toggleSidebar());
        return;
      }
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT'))
        return;
      if (e.key === '1') dispatch(setScenarioStage(1));
      else if (e.key === '2') dispatch(setScenarioStage(2));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dispatch]);

  // The detail panel is open by default on entering stage 2 (desktop and
  // tablet) and closed at stage 1. Phones start collapsed. Keyed on stage
  // only, so closing it by hand sticks.
  useEffect(() => {
    if (stage === 2) {
      if (!isCompactViewport()) dispatch(openSidebar());
    } else {
      dispatch(closeSidebar());
    }
  }, [stage, dispatch]);

  // Selecting a lit node opens its evidence drawer. Deps deliberately
  // exclude sidebarOpen — re-opening on every close would trap the user.
  useEffect(() => {
    if (stage === 2 && selectedNode) dispatch(openSidebar());
  }, [stage, selectedNode, dispatch]);

  const open = stage === 2 && sidebarOpen;

  return (
    <div className={`fv2-scenario-view fv2-at-stage-${stage}${open ? ' fv2-sb-open' : ''}`}>
      <div className="simulation-layout">
        <SimulationCanvas />
        {stage === 2 && <StoryRail />}
        <div className="fv2-hud fv2-hud-top">
          {stage === 2 && <VerdictBanner />}
          <ZoomControls />
          {stage === 2 && (
            <button
              type="button"
              className="fv2-detail-toggle"
              aria-pressed={sidebarOpen}
              onClick={() => dispatch(toggleSidebar())}
              title={`Toggle the detail panel (${DETAIL_SHORTCUT})`}
            >
              DETAILS
            </button>
          )}
        </div>
        {stage === 2 && (
          <div className="fv2-hud fv2-hud-bottom">
            <PaletteLegend />
            <PlaybackToolbar />
          </div>
        )}
        {stage === 2 && !sidebarOpen && (
          <div className="fv2-canvas-hint">
            SELECT ANY LIT NODE FOR ITS EVIDENCE · {DETAIL_SHORTCUT} TOGGLES THE DETAIL PANEL
          </div>
        )}
        {sidebarOpen && (
          <div className="sidebar-backdrop" onClick={() => dispatch(closeSidebar())} />
        )}
        <aside className={`simulation-sidebar${sidebarOpen ? ' sidebar-open' : ''}`}>
          <AssertionSummaryPanel />
          <EventInspectorPanel />
        </aside>
      </div>
      {stage === 1 && <Stage1Card />}
    </div>
  );
}

function FacetApp() {
  const view = useSelector(selectAppView);

  return (
    <SimulationShell>
      <TopBar />
      {view !== 'landing' && <LiveBar />}
      <WebSocketErrorOverlay />
      {view === 'landing' && <LandingView />}
      {view === 'portfolio' && <PortfolioLedger />}
      {view === 'flow' && <FlowLedger />}
      {view === 'diff' && <ScenarioDiffView />}
      {view === 'impact' && <ImpactAnalysisPanel />}
      {view === 'scenario' && <ScenarioWorkspace />}
    </SimulationShell>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<FacetApp />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
